/**
 * File extension extraction and validation utilities.
 * Handles simple and compound extensions (e.g., .tar.gz).
 *
 * @module extension-utils
 */

import {
    ARCHIVE_EXTENSIONS,
    ALL_EXTENSIONS_SET,
    DISALLOWED_EXTENSIONS,
    THUMBNAIL_SUPPORTED_EXTENSIONS_SET,
} from '../media/media-extensions';

/**
 * Get the file extension using a simple fallback method.
 * Returns everything after the last dot, or the full filename if no dot.
 * @param fileName - The filename to extract extension from
 */
export function getExtensionSimple(fileName: string): string {
    if (fileName.lastIndexOf('.') === -1) {
        return fileName;
    }
    return fileName.substring(fileName.lastIndexOf('.') + 1);
}

/**
 * Get the file extension, handling compound extensions like .tar.gz.
 * Preserves the case of the original extension.
 * @param fileName - The filename to extract extension from
 */
export function getExtension(fileName: string): string {
    // Check for compound archive extensions first (e.g., .tar.gz, .tar.bz2)
    const upperFileName = fileName.toUpperCase();
    const compoundExt = ARCHIVE_EXTENSIONS.find(ext => upperFileName.endsWith(ext));

    if (compoundExt) {
        // Return extension in original case
        return fileName.substring(fileName.length - compoundExt.length);
    }

    return getExtensionSimple(fileName);
}

/**
 * Get extensions for multiple filenames.
 * @param filenames - Array of filenames
 * @returns Array of extensions
 */
export function getExtensionList(filenames: string[]): string[] {
    return filenames.map(getExtension);
}

/**
 * Check if the file has a known supported extension.
 * @param filename - The filename to check
 */
export function isKnownSupportedExtension(filename: string): boolean {
    const ext = getExtension(filename).toUpperCase();
    return ALL_EXTENSIONS_SET.has(ext as any);
}

/**
 * Check if the file extension is disallowed (for security reasons).
 * @param filename - The filename to check
 */
export function isExtensionDisallowed(filename: string): boolean {
    const ext = getExtension(filename).toUpperCase();
    return DISALLOWED_EXTENSIONS.has(ext);
}

/**
 * Check if the file extension is allowed (not in the disallowed list).
 * @param filename - The filename to check
 */
export function isExtensionAllowed(filename: string): boolean {
    return !isExtensionDisallowed(filename);
}

/**
 * Check if thumbnail generation is supported for this file.
 * @param filename - The filename to check
 */
export function isThumbnailSupported(filename: string): boolean {
    const ext = getExtension(filename).toUpperCase();
    return THUMBNAIL_SUPPORTED_EXTENSIONS_SET.has(ext as any);
}

/**
 * Normalize an extension (uppercase, no leading dot).
 * @param extension - The extension to normalize
 */
export function normalizeExtension(extension: string): string {
    return extension.toUpperCase().replace(/^\./, '');
}

/**
 * Check if two extensions are equivalent (case-insensitive).
 * @param ext1 - First extension
 * @param ext2 - Second extension
 */
export function extensionsMatch(ext1: string, ext2: string): boolean {
    return normalizeExtension(ext1) === normalizeExtension(ext2);
}
